import React, { useMemo } from 'react';
import { WeatherWidgetRenderProps } from '@mono/types/src/weather-widgets';
import { PlatMapSettings } from '@mono/types/src/platmap';
import { SettingsContext } from '@mono/platmap-settings-context';
import { mapboxAccessTokenSk } from '@mono/mapbox-service-api';
import { toHash } from '@mono/to-hash';
import { LightningContextProvider } from '@mono/lightning-context';
import { MapWrap } from './MapWrap';
import { MapboxServiceContextProvider } from './mapbox-service-context/MapboxServiceContext';
import { WXTilesServiceContextProvider } from './wxtiles-service-context/WXTilesServiceContext';
import { WXTilesV3ServiceContextProvider } from './wxtilesv3-service-context/WXTilesV3ServiceContext';
import { GeojsonContextProvider } from './geojson-context/GeojsonContext';
import { delimiter, anchorId } from './constants';
import { LightningServiceContextProvider } from './lightning-service-context/LightningServiceContext';
import { TimeContextProvider } from './time-context';
import styled from 'styled-components';

export const PlatMap = (props: WeatherWidgetRenderProps<PlatMapSettings>) => {
    const migratedSettings = useMemo(() => {
        if (props.settings.timeControl) return props.settings;

        return {
            ...props.settings,
            timeControl: {
                type: 'timeoffset' as const,
                offset: props.settings.timeOffset ?? 'PT-6H',
            },
        };
    }, [toHash(props.settings)]);

    return (
        <StyledWrapper style={{ width: '100%', height: '100%' }} isRenderingForForecast={props.isRenderingForForecast}>
            <SettingsContext.Provider value={{ settings: migratedSettings }}>
                <TimeContextProvider stage={props.stage}>
                    <LightningContextProvider {...{ metjwt: props.metjwt }}>
                        <MapboxServiceContextProvider {...{ delimiter, anchorId, mapboxAccessTokenSk }}>
                            <WXTilesServiceContextProvider {...{ delimiter, anchorId }} serviceEndpoint={props.settings.wxtiles.serviceEndpoint}>
                                <WXTilesV3ServiceContextProvider {...{ delimiter, anchorId, stage: props.stage }}>
                                    <GeojsonContextProvider {...{ delimiter, anchorId }}>
                                        <LightningServiceContextProvider {...{ delimiter, anchorId, stage: props.stage }}>
                                            <MapWrap
                                                renderingForPDF={props.isRenderingForPDF}
                                                userTracking={props.userTracking}
                                                renderingForForecast={props.isRenderingForForecast}
                                                theme={props.theme}
                                            />
                                        </LightningServiceContextProvider>
                                    </GeojsonContextProvider>
                                </WXTilesV3ServiceContextProvider>
                            </WXTilesServiceContextProvider>
                        </MapboxServiceContextProvider>
                    </LightningContextProvider>
                </TimeContextProvider>
            </SettingsContext.Provider>
        </StyledWrapper>
    );
};

const StyledWrapper = styled.div<{ isRenderingForForecast?: boolean }>`
    .mapboxgl-ctrl-group button {
        width: 32px;
        height: 32px;
        background-color: #7599ff;
    }

    .mapboxgl-ctrl-group button:first-child {
        border-top-left-radius: 4px;
        border-top-right-radius: 4px;
    }

    .mapboxgl-ctrl-group button:last-child {
        border-bottom-left-radius: 4px;
        border-bottom-right-radius: 4px;
    }

    .mapboxgl-ctrl-group button span {
        filter: invert(100%) brightness(1000%); /* mapbox loads their icons as images, so I use css filters to make them white */
    }

    .mapboxgl-ctrl-group button:hover {
        background-color: #7599ff !important;
    }

    .mapboxgl-ctrl.mapboxgl-ctrl-group {
        box-shadow: none;
        background: none;
        border-radius: 4px;
    }
    ${({ isRenderingForForecast }) =>
        !isRenderingForForecast &&
        `.mapboxgl-ctrl-top-right {
    top: 32px;
    right: 2px;
  }`}
`;
